/**
 * <swiper-init> — Custom Element that auto-initializes a native Swiper 12
 * instance on connectedCallback. Reads config from `data-swiper-settings`
 * JSON either on `this` (element itself is the Swiper root) or on the first
 * `[data-swiper-settings]` child. Optional thumb swiper via
 * `data-swiper-thumb="<selector>"` + `data-swiper-thumb-settings='<json>'`
 * on the thumb element.
 *
 * Usage in liquid:
 *   <swiper-init class="swiper" data-swiper-settings='{{ swiper_options }}'>
 *     <div class="swiper-wrapper">...</div>
 *   </swiper-init>
 *
 * Public API:
 *   element.swiper         — native Swiper 12 instance (set by Swiper itself
 *                            on `mount()`; read after `swiper:initialized`)
 *   element.thumbSwiper    — native thumb Swiper instance (if any)
 *
 * Dispatched events (bubbles):
 *   swiper:initialized  → { swiper, thumbs }
 *   swiper:destroyed
 *   swiper:slide-changed → { currentIndex, element }
 *
 * Loaded as ES module via `@theme/swiper-init` importmap entry; also loaded
 * globally from `layout/theme.liquid` so every page has the CE defined.
 *
 * Swiper itself is imported directly as an ES module — it is only fetched
 * on pages that actually use a `<swiper-init>` element (lazy load).
 */

import Swiper from '@theme/swiper';

class SwiperInit extends HTMLElement {
  thumbSwiper = null;
  #rootEl = null;
  #thumbEl = null;

  connectedCallback() {
    this.#rootEl = this.hasAttribute('data-swiper-settings')
      ? this
      : this.querySelector('[data-swiper-settings]');

    if (!this.#rootEl) {
      console.warn('<swiper-init>: no [data-swiper-settings] found', this);
      return;
    }

    const thumbSelector = this.getAttribute('data-swiper-thumb');
    if (thumbSelector) {
      this.#thumbEl = document.querySelector(thumbSelector);
    }

    this.#init();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #init() {
    let config;
    try {
      config = JSON.parse(this.#rootEl.getAttribute('data-swiper-settings'));
    } catch (e) {
      console.error('<swiper-init>: invalid data-swiper-settings JSON', e);
      return;
    }

    if (this.#thumbEl) {
      const thumbConfig = JSON.parse(
        this.#thumbEl.getAttribute('data-swiper-thumb-settings') || '{}'
      );
      this.thumbSwiper = new Swiper(this.#thumbEl, thumbConfig);
      config.thumbs = { ...(config.thumbs || {}), swiper: this.thumbSwiper };
    }

    // Swiper sets `this.#rootEl.swiper = <instance>` internally on mount().
    // When #rootEl === this (the Custom Element), that means `this.swiper`
    // becomes the native Swiper instance automatically.
    const swiper = new Swiper(this.#rootEl, config);

    this.#revealActiveSlide(swiper);

    swiper.on('slideChange', () => {
      this.dispatchEvent(
        new CustomEvent('swiper:slide-changed', {
          bubbles: true,
          detail: { currentIndex: swiper.activeIndex, element: swiper.el },
        })
      );
    });

    this.dispatchEvent(
      new CustomEvent('swiper:initialized', {
        bubbles: true,
        detail: { swiper, thumbs: this.thumbSwiper },
      })
    );
  }

  /**
   * Opt-in via `data-swiper-scroll-active="<selector>"`: after mount, jump (no
   * animation) so the slide containing the matching element is fully in view.
   * Used by nav bars (collection/search tabs) so the active tab is never left
   * off-screen or clipped when the page loads deep in the list. Skips the jump
   * when the active slide already fits — early tabs keep the natural start view.
   */
  #revealActiveSlide(swiper) {
    const activeSelector = this.getAttribute('data-swiper-scroll-active');
    if (activeSelector == null) return;

    const slide = this.#rootEl
      .querySelector(activeSelector || '.is-active')
      ?.closest('.swiper-slide');
    const index = slide ? swiper.slides.indexOf(slide) : -1;
    if (index < 0) return;

    const viewport = swiper.el.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const fullyVisible = rect.left >= viewport.left - 1 && rect.right <= viewport.right + 1;
    if (fullyVisible) return;

    swiper.slideTo(index, 0, false);
  }

  #destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }
    this.dispatchEvent(new CustomEvent('swiper:destroyed', { bubbles: true }));
  }
}

if (!customElements.get('swiper-init')) {
  customElements.define('swiper-init', SwiperInit);
}
